import {
  addDoc,
  collection,
  doc,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { PLATFORM_COLLECTIONS } from './platformSchema';
import { loadRecordsByEquality } from './firestoreQueryHelpers';
import {
  getBookingCommercialState,
  isBookingCoveredByPlanOrPackage,
} from './booking';
import {
  notifyBookingPaymentPending,
  notifyBookingPaymentReceived,
  notifyBookingPaymentFailed,
  notifyBookingRefundIssued,
  notifyBookingCoverageApplied,
} from './notifications';

function normalizeString(value = '') {
  return String(value || '').trim();
}

function normalizeNumber(value, fallback = null) {
  if (value === '' || value === null || typeof value === 'undefined') return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function toCurrencyCode(value = 'USD') {
  return normalizeString(value || 'USD').toUpperCase() || 'USD';
}

function buildProviderId(prefix = 'provider') {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function isSettledStatus(status = '') {
  return ['authorized', 'paid', 'partially_paid', 'waived'].includes(status);
}

export function normalizeBookingPayment(record = {}, fallbackId = '') {
  const id = record.id || record.bookingPaymentId || fallbackId || '';

  return {
    id,
    bookingPaymentId: id,
    organizationId: record.organizationId || '',
    bookingId: record.bookingId || '',
    bookingRequestId: record.bookingRequestId || '',
    requesterUserId: record.requesterUserId || '',
    requesterEmail: normalizeString(record.requesterEmail || ''),
    amount: normalizeNumber(record.amount, 0) || 0,
    currency: toCurrencyCode(record.currency || 'USD'),
    status: normalizeString(record.status || 'unpaid'),
    paymentType: normalizeString(record.paymentType || 'full'),
    provider: normalizeString(record.provider || 'placeholder'),
    providerSessionId: normalizeString(record.providerSessionId || ''),
    providerPaymentIntentId: normalizeString(record.providerPaymentIntentId || ''),
    providerCheckoutUrl: normalizeString(record.providerCheckoutUrl || ''),
    billingSourceType: normalizeString(record.billingSourceType || ''),
    billingSourceId: normalizeString(record.billingSourceId || ''),
    packageId: normalizeString(record.packageId || ''),
    createdAt: record.createdAt || null,
    updatedAt: record.updatedAt || null,
    paidAt: record.paidAt || null,
    refundedAt: record.refundedAt || null,
    metadata: record.metadata || {},
  };
}

export function calculateBookingAmount(booking = {}, { defaultAmount = 0 } = {}) {
  const commercialState = getBookingCommercialState(booking);
  const priceSnapshot = normalizeNumber(commercialState.priceSnapshot, null);
  const servicePricingSnapshot = normalizeNumber(booking.servicePricingSnapshot, null);
  return priceSnapshot !== null
    ? priceSnapshot
    : servicePricingSnapshot !== null
      ? servicePricingSnapshot
      : normalizeNumber(defaultAmount, 0) || 0;
}

export function calculateDepositAmount(booking = {}, { depositRate = 0.25 } = {}) {
  const commercialState = getBookingCommercialState(booking);
  const explicitDeposit = normalizeNumber(commercialState.depositAmountSnapshot, null);
  if (explicitDeposit !== null) return explicitDeposit;
  if (!commercialState.depositRequired) return 0;

  const fullAmount = calculateBookingAmount(booking);
  return Number((fullAmount * depositRate).toFixed(2));
}

export function resolveBookingCoverage(booking = {}) {
  const commercialState = getBookingCommercialState(booking);

  if (commercialState.paymentStatus === 'waived' || commercialState.billingSourceType === 'manual_waiver') {
    return {
      covered: true,
      sourceType: 'waived',
      sourceId: commercialState.billingSourceId || '',
      reason: 'waived',
    };
  }

  if (
    commercialState.packageId ||
    commercialState.billingSourceType === 'package' ||
    commercialState.packageCoverageAmount > 0
  ) {
    const priceSnapshot = calculateBookingAmount(booking);
    const packageCoverageAmount = normalizeNumber(commercialState.packageCoverageAmount, 0) || 0;
    const fullyCovered = packageCoverageAmount > 0
      ? packageCoverageAmount >= priceSnapshot && priceSnapshot > 0
      : true;
    return {
      covered: fullyCovered,
      sourceType: 'package',
      sourceId: commercialState.packageId || commercialState.billingSourceId || '',
      reason: fullyCovered ? 'package_coverage' : 'partial_package_coverage',
      partial: packageCoverageAmount > 0 && !fullyCovered,
      amountCovered: packageCoverageAmount,
    };
  }

  if (isBookingCoveredByPlanOrPackage(booking)) {
    return {
      covered: true,
      sourceType: commercialState.billingSourceType || 'organization_subscription',
      sourceId: commercialState.billingSourceId || '',
      reason: 'plan_or_subscription_coverage',
    };
  }

  return {
    covered: false,
    sourceType: '',
    sourceId: '',
    reason: '',
  };
}

export function isBookingCoveredByPackage(booking = {}) {
  return resolveBookingCoverage(booking).sourceType === 'package';
}

export function isBookingCoveredByPlan(booking = {}) {
  const coverage = resolveBookingCoverage(booking);
  return coverage.covered && coverage.sourceType !== 'package' && coverage.sourceType !== 'waived';
}

export function doesBookingRequirePayment(booking = {}) {
  const coverage = resolveBookingCoverage(booking);
  if (coverage.covered) return false;

  const commercialState = getBookingCommercialState(booking);
  const amount = calculateBookingAmount(booking);
  const packageCoverageAmount = normalizeNumber(commercialState.packageCoverageAmount, 0) || 0;

  if (amount <= 0 || amount <= packageCoverageAmount) return false;
  if (['paid', 'authorized', 'waived'].includes(commercialState.paymentStatus)) return false;
  return true;
}

export function getBookingOutstandingBalance(booking = {}, payments = []) {
  if (!doesBookingRequirePayment(booking)) return 0;

  const commercialState = getBookingCommercialState(booking);
  const amount = Math.max(
    Number((calculateBookingAmount(booking) - (normalizeNumber(commercialState.packageCoverageAmount, 0) || 0)).toFixed(2)),
    0
  );
  const paidAmount = (payments || [])
    .map((payment) => normalizeBookingPayment(payment))
    .filter((payment) => isSettledStatus(payment.status))
    .reduce((sum, payment) => sum + (payment.amount || 0), 0);

  return Math.max(Number((amount - paidAmount).toFixed(2)), 0);
}

export function isBookingReadyForConfirmation(
  booking = {},
  {
    payments = [],
    requireDepositBeforeConfirmation = false,
  } = {}
) {
  if (!doesBookingRequirePayment(booking)) {
    return { ok: true, reason: '' };
  }

  if (!requireDepositBeforeConfirmation) {
    return { ok: true, reason: 'confirmation_can_precede_payment' };
  }

  const commercialState = getBookingCommercialState(booking);
  if (!commercialState.depositRequired) {
    return getBookingOutstandingBalance(booking, payments) <= 0
      ? { ok: true, reason: '' }
      : { ok: false, reason: 'full_payment_required_before_confirmation' };
  }

  const depositAmount = calculateDepositAmount(booking);
  const qualifyingAmount = (payments || [])
    .map((payment) => normalizeBookingPayment(payment))
    .filter((payment) => isSettledStatus(payment.status))
    .filter((payment) => ['deposit', 'full', 'manual', 'package'].includes(payment.paymentType))
    .reduce((sum, payment) => sum + (payment.amount || 0), 0);

  return qualifyingAmount >= depositAmount
    ? { ok: true, reason: '' }
    : { ok: false, reason: 'deposit_required_before_confirmation' };
}

async function syncBookingPaymentState(db, booking = {}, payments = [], extraFields = {}) {
  const bookingId = booking.bookingId || booking.id || '';
  if (!bookingId) return null;

  const normalizedPayments = (payments || []).map((payment) => normalizeBookingPayment(payment));
  const commercialState = getBookingCommercialState(booking);
  const outstandingBalance = getBookingOutstandingBalance(booking, normalizedPayments);
  const paidAmount = normalizedPayments
    .filter((payment) => isSettledStatus(payment.status))
    .reduce((sum, payment) => sum + (payment.amount || 0), 0);

  let paymentStatus = commercialState.paymentStatus || 'unpaid';
  if (resolveBookingCoverage(booking).covered) {
    paymentStatus = commercialState.paymentStatus === 'waived' ? 'waived' : 'paid';
  } else if (paidAmount > 0 && outstandingBalance > 0) {
    paymentStatus = 'partially_paid';
  } else if (outstandingBalance <= 0 && paidAmount > 0) {
    paymentStatus = 'paid';
  } else if (normalizedPayments.some((payment) => payment.status === 'failed')) {
    paymentStatus = 'failed';
  } else if (normalizedPayments.some((payment) => ['pending', 'authorized'].includes(payment.status))) {
    paymentStatus = normalizedPayments.some((payment) => payment.status === 'authorized') ? 'authorized' : 'pending';
  }

  const updatePayload = {
    paymentStatus,
    amountPaid: Number(paidAmount.toFixed(2)),
    outstandingBalance,
    updatedAt: serverTimestamp(),
    ...extraFields,
  };

  await updateDoc(doc(db, PLATFORM_COLLECTIONS.BOOKINGS, bookingId), updatePayload);
  return updatePayload;
}

export async function createBookingPayment(
  db,
  {
    booking = {},
    paymentType = 'full',
    amount = null,
    currency = 'USD',
    provider = 'placeholder',
    billingSourceType = '',
    billingSourceId = '',
    packageId = '',
    metadata = {},
  } = {}
) {
  const commercialState = getBookingCommercialState(booking);
  const normalizedAmount = normalizeNumber(
    amount,
    paymentType === 'deposit' ? calculateDepositAmount(booking) : calculateBookingAmount(booking)
  ) || 0;

  const payload = normalizeBookingPayment({
    organizationId: booking.organizationId || '',
    bookingId: booking.bookingId || booking.id || '',
    bookingRequestId: booking.bookingRequestId || '',
    requesterUserId: booking.requesterUserId || '',
    requesterEmail: booking.requesterEmail || '',
    amount: normalizedAmount,
    currency: currency || commercialState.currency || 'USD',
    status: normalizedAmount > 0 ? 'pending' : 'waived',
    paymentType,
    provider,
    billingSourceType: billingSourceType || commercialState.billingSourceType || '',
    billingSourceId: billingSourceId || commercialState.billingSourceId || '',
    packageId: packageId || commercialState.packageId || '',
    metadata,
  });

  const docRef = (await addDoc(collection(db, PLATFORM_COLLECTIONS.BOOKING_PAYMENTS), {
    ...payload,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })) || {};

  const bookingPayment = normalizeBookingPayment(payload, docRef.id || '');
  await syncBookingPaymentState(db, booking, [bookingPayment]);
  return bookingPayment;
}

export async function getBookingPayments(db, bookingId = '') {
  if (!bookingId) return [];
  const records = await loadRecordsByEquality(db, {
    collectionName: PLATFORM_COLLECTIONS.BOOKING_PAYMENTS,
    field: 'bookingId',
    value: bookingId,
  });
  return records.map((record) => normalizeBookingPayment(record));
}

export async function getRequesterBookingPayments(db, requesterUserId = '') {
  if (!requesterUserId) return [];
  const records = await loadRecordsByEquality(db, {
    collectionName: PLATFORM_COLLECTIONS.BOOKING_PAYMENTS,
    field: 'requesterUserId',
    value: requesterUserId,
  });
  return records.map((record) => normalizeBookingPayment(record));
}

export async function getOrganizationBookingPayments(db, organizationId = '') {
  if (!organizationId) return [];
  const records = await loadRecordsByEquality(db, {
    collectionName: PLATFORM_COLLECTIONS.BOOKING_PAYMENTS,
    field: 'organizationId',
    value: organizationId,
  });
  return records.map((record) => normalizeBookingPayment(record));
}

export async function getInstructorBookingPayments(db, instructorId = '') {
  if (!instructorId) return [];
  const bookingRecords = await loadRecordsByEquality(db, {
    collectionName: PLATFORM_COLLECTIONS.BOOKINGS,
    field: 'instructorId',
    value: instructorId,
  });
  const bookingIds = new Set((bookingRecords || []).map((booking) => booking.bookingId || booking.id).filter(Boolean));
  if (!bookingIds.size) return [];

  const payments = [];
  await Promise.all(
    Array.from(bookingIds).map(async (bookingId) => {
      const bookingPayments = await getBookingPayments(db, bookingId);
      payments.push(...bookingPayments);
    })
  );
  return payments;
}

export async function updateBookingPaymentStatus(
  db,
  paymentId = '',
  {
    status = 'pending',
    booking = {},
    metadata = null,
    paidAt = null,
    refundedAt = null,
  } = {}
) {
  if (!paymentId) throw new Error('Payment id is required.');
  const updatePayload = {
    status,
    updatedAt: serverTimestamp(),
  };
  if (metadata) updatePayload.metadata = metadata;
  if (paidAt !== null) updatePayload.paidAt = paidAt;
  if (refundedAt !== null) updatePayload.refundedAt = refundedAt;

  await updateDoc(doc(db, PLATFORM_COLLECTIONS.BOOKING_PAYMENTS, paymentId), updatePayload);

  const payments = await getBookingPayments(db, booking.bookingId || booking.id || '');
  await syncBookingPaymentState(db, booking, payments);
  return {
    paymentId,
    status,
  };
}

export async function attachCheckoutSessionToBookingPayment(
  db,
  paymentId = '',
  {
    booking = {},
    provider = 'stripe',
    providerSessionId = '',
    providerPaymentIntentId = '',
    providerCheckoutUrl = '',
  } = {}
) {
  if (!paymentId) throw new Error('Payment id is required.');

  await updateDoc(doc(db, PLATFORM_COLLECTIONS.BOOKING_PAYMENTS, paymentId), {
    provider,
    providerSessionId,
    providerPaymentIntentId,
    providerCheckoutUrl,
    status: 'pending',
    updatedAt: serverTimestamp(),
  });

  const payments = await getBookingPayments(db, booking.bookingId || booking.id || '');
  await syncBookingPaymentState(db, booking, payments);
  return {
    paymentId,
    provider,
    providerSessionId,
    providerPaymentIntentId,
    providerCheckoutUrl,
  };
}

export async function markBookingPaymentPaid(db, { payment = {}, booking = {}, actorUserId = '', actorName = '' } = {}) {
  const normalizedPayment = normalizeBookingPayment(payment);
  await updateBookingPaymentStatus(db, normalizedPayment.bookingPaymentId, {
    status: 'paid',
    booking,
    paidAt: serverTimestamp(),
  });
  try {
    await notifyBookingPaymentReceived(db, {
      booking,
      payment: normalizedPayment,
      actorUserId,
      actorName,
    });
  } catch (error) {
    console.warn('Booking payment received notification failed:', error);
  }
  return { paymentId: normalizedPayment.bookingPaymentId, status: 'paid' };
}

export async function markBookingPaymentFailed(db, { payment = {}, booking = {}, actorUserId = '', actorName = '' } = {}) {
  const normalizedPayment = normalizeBookingPayment(payment);
  await updateBookingPaymentStatus(db, normalizedPayment.bookingPaymentId, {
    status: 'failed',
    booking,
  });
  try {
    await notifyBookingPaymentFailed(db, {
      booking,
      payment: normalizedPayment,
      actorUserId,
      actorName,
    });
  } catch (error) {
    console.warn('Booking payment failure notification failed:', error);
  }
  return { paymentId: normalizedPayment.bookingPaymentId, status: 'failed' };
}

export async function markBookingPaymentRefunded(db, { payment = {}, booking = {}, actorUserId = '', actorName = '' } = {}) {
  const normalizedPayment = normalizeBookingPayment(payment);
  await updateBookingPaymentStatus(db, normalizedPayment.bookingPaymentId, {
    status: 'refunded',
    booking,
    refundedAt: serverTimestamp(),
  });
  try {
    await notifyBookingRefundIssued(db, {
      booking,
      payment: normalizedPayment,
      actorUserId,
      actorName,
    });
  } catch (error) {
    console.warn('Booking refund notification failed:', error);
  }
  return { paymentId: normalizedPayment.bookingPaymentId, status: 'refunded' };
}

export async function markBookingPaymentWaived(db, { payment = {}, booking = {}, actorUserId = '', actorName = '' } = {}) {
  const normalizedPayment = normalizeBookingPayment(payment);
  await updateBookingPaymentStatus(db, normalizedPayment.bookingPaymentId, {
    status: 'waived',
    booking,
  });
  try {
    await notifyBookingCoverageApplied(db, {
      booking: {
        ...booking,
        paymentStatus: 'waived',
      },
      actorUserId,
      actorName,
    });
  } catch (error) {
    console.warn('Booking waiver notification failed:', error);
  }
  return { paymentId: normalizedPayment.bookingPaymentId, status: 'waived' };
}

export async function createBookingCheckoutSessionScaffold(
  db,
  {
    booking = {},
    paymentType = 'full',
    provider = 'stripe',
    amount = null,
    currency = 'USD',
    actorUserId = '',
    actorName = '',
  } = {}
) {
  const payment = await createBookingPayment(db, {
    booking,
    paymentType,
    amount,
    currency,
    provider,
    metadata: {
      scaffolded: true,
      actorUserId,
    },
  });

  const providerSessionId = buildProviderId('checkout');
  const providerPaymentIntentId = buildProviderId('intent');
  const providerCheckoutUrl = `/my-bookings?checkout=${providerSessionId}`;
  const paymentId = payment.bookingPaymentId || payment.id || '';

  await attachCheckoutSessionToBookingPayment(db, paymentId, {
    booking,
    provider,
    providerSessionId,
    providerPaymentIntentId,
    providerCheckoutUrl,
  });

  try {
    await notifyBookingPaymentPending(db, {
      booking: {
        ...booking,
        paymentStatus: 'pending',
      },
      actorUserId,
      actorName,
    });
  } catch (error) {
    console.warn('Booking payment pending notification failed:', error);
  }

  return {
    paymentId,
    provider,
    providerSessionId,
    providerPaymentIntentId,
    providerCheckoutUrl,
  };
}

export async function applyBookingPackageCoverage(
  db,
  {
    booking = {},
    packageId = '',
    billingSourceId = '',
    actorUserId = '',
    actorName = '',
  } = {}
) {
  const payment = await createBookingPayment(db, {
    booking: {
      ...booking,
      billingSourceType: 'package',
      billingSourceId,
      packageId,
      paymentStatus: 'paid',
    },
    paymentType: 'package',
    amount: calculateBookingAmount(booking),
    currency: getBookingCommercialState(booking).currency || 'USD',
    provider: 'coverage',
    billingSourceType: 'package',
    billingSourceId,
    packageId,
  });

  await markBookingPaymentWaived(db, {
    payment,
    booking: {
      ...booking,
      billingSourceType: 'package',
      billingSourceId,
      packageId,
    },
    actorUserId,
    actorName,
  });

  return payment;
}

export async function applyBookingDepositPolicy(
  db,
  {
    booking = {},
    depositRate = 0.25,
  } = {}
) {
  const depositAmountSnapshot = calculateDepositAmount({
    ...booking,
    depositRequired: true,
  }, { depositRate });

  const updatePayload = {
    depositRequired: true,
    depositStatus: 'pending',
    depositAmountSnapshot,
    updatedAt: serverTimestamp(),
  };

  await updateDoc(doc(db, PLATFORM_COLLECTIONS.BOOKINGS, booking.bookingId || booking.id || ''), updatePayload);
  return updatePayload;
}
